import _ from 'lodash';
import moment from 'moment';
import { lsApiService } from 'ls-vue-common';
// eslint-disable-next-line import/no-cycle
import store from '@/store';
// eslint-disable-next-line import/no-cycle
import router from '@/router';

const _private = {
    engagement_id: null,
    engagement_id_set_at: null,
    aliasRequestProgress: {
        sending: false,
        success: false,
    },
    identifiedSegmentId: null,
};

_private.engagementIdIsExpired = function () {
    const eid_ttl_mins = 120;
    return (
        !_private.engagement_id_set_at ||
        moment.duration(moment().diff(_private.engagement_id_set_at)).asMinutes() > eid_ttl_mins
    );
};

_private.getEngagementId = function () {
    if (!_private.engagement_id || _private.engagementIdIsExpired()) {
        _private.engagement_id = null;
        _private.engagement_id_set_at = null;
    }
    return _private.engagement_id;
};

_private.identify = function (id, name) {
    if (!_.isFunction(_.get(window, 'analytics.identify'))) {
        return;
    }

    window.analytics.ready(() => {
        const segmentAnonId = window.analytics.user().anonymousId();

        if (segmentAnonId !== _private.identifiedSegmentId) {
            const identityTraits = { name };
            _private.identifiedSegmentId = segmentAnonId;
            window.analytics.identify(`${id}`, identityTraits);
        }
    });
};

_private.alias = function (id, user) {
    if (_private.aliasRequestProgress.sending || _private.aliasRequestProgress.success) {
        return;
    }

    if (_.isFunction(_.get(window, 'analytics.alias'))) {
        window.analytics.alias(`${id}`);
    }
    if (user) {
        lsApiService
            .request({
                method: 'PUT',
                route: `/users/${user.user_id}`,
                payload: { is_mixpanel_aliased: true },
            })
            .then(() => {
                // eslint-disable-next-line no-param-reassign
                user.is_mixpanel_aliased = true;
                _private.aliasRequestProgress.success = true;
                _private.aliasRequestProgress.sending = false;
            })
            .catch(() => {
                _private.aliasRequestProgress.sending = false;
            });

        // with segment call identify more often than less often
        _private.identify(id, `${user.first_name} ${user.last_name}`);
    }
};

_private.addSegmentUUIDToOrder = function (order, segment_anonymous_id) {
    lsApiService.request({
        method: 'POST',
        route: `/orders/${order.order_id}/uuid`,
        payload: { segment_anonymous_id },
    });
};

function track(event) {
    if (!_.isFunction(_.get(window, 'analytics.track'))) {
        return;
    }
    const data = event.data || {};
    const eid = _private.getEngagementId();
    if (eid) {
        data.eid = eid;
    }
    const appName = process.env.VUE_APP_NAME;
    const appVersion = process.env.VUE_APP_VERSION;
    const target = event.target || 'unknown event';
    const state = event.state || router.history.current.name;
    const order_id = _.get(event, 'data.order_id') || _.get(store, 'state.order.order_id');
    _.assign(data, {
        appName,
        appVersion,
        state,
        target,
        order_id,
    });
    window.analytics.track(event.type, data);
}

function page(data = {}) {
    if (!_.isFunction(_.get(window, 'analytics.page'))) {
        return;
    }
    const path = data.path || location.pathname || ''; // eslint-disable-line no-restricted-globals
    const referrer = data.referrer || '';
    const search = data.search || location.search || ''; // eslint-disable-line no-restricted-globals
    const url = data.url || location.origin + path || ''; // eslint-disable-line no-restricted-globals
    window.analytics.page({
        path,
        referrer,
        search,
        url,
    });
}

function aliasOrIdentify(user) {
    if (!user || _.isUndefined(user.is_mixpanel_aliased)) {
        return;
    }

    if (!user.is_mixpanel_aliased) {
        _private.alias(user.hash_id, user);
    } else {
        _private.identify(user.hash_id, `${user.first_name} ${user.last_name}`);
    }
}

function setEngagementId(eid) {
    _private.engagement_id = eid;
    _private.engagement_id_set_at = moment();
}

function getAnonymousId() {
    if (!_.isFunction(_.get(window, 'analytics.user'))) {
        return;
    }
    const user = window.analytics.user();
    return user.anonymousId() || null; // eslint-disable-line
}

function waitAndGetAnonymousId() {
    return new Promise((resolve) => {
        if (_.get(window, 'analytics.ready')) {
            window.analytics.ready(() => {
                resolve(window.analytics.user().anonymousId() || null);
            });
        }
    });
}

function recordAnonymousIdToOrder(createdOrderResponse) {
    const order = _.get(createdOrderResponse, 'data');
    if (!_.get(order, 'order_id')) {
        return;
    }
    const segmentAnonId = this.getAnonymousId();
    if (segmentAnonId) {
        _private.addSegmentUUIDToOrder(order, segmentAnonId);
    } else {
        this.waitAndGetAnonymousId().then((anonId) => {
            if (anonId) {
                _private.addSegmentUUIDToOrder(order, anonId);
            }
        });
    }
}

function sendGoogleAnalyticsPageview(pageName) {
    if (!_.isFunction(_.get(window, 'analytics.page'))) {
        return;
    }
    window.analytics.page({ path: pageName }); // sending a single path object to segment, which then passes to GA
}

function trackGoogleConversion(evt) {
    if (!_.isFunction(_.get(window, 'analytics.ready'))) {
        return;
    }
    window.analytics.ready(() => {
        if (!_.isFunction(window.google_trackConversion)) {
            return;
        }
        window.google_trackConversion({
            google_conversion_id: evt.id,
            google_conversion_language: evt.language || 'en',
            google_conversion_format: evt.format || '3',
            google_conversion_color: evt.color || 'ffffff',
            google_conversion_label: evt.label,
            google_conversion_value: evt.value,
            google_remarketing_only: evt.marketing_only || false,
        });
    });
}

function initiateCheckout({ order_id }) {
    track({
        type: 'InitiateCheckout',
        data: {
            order_id,
        },
    });
    sendGoogleAnalyticsPageview('/lead'); // sending this pageview for now until confirmation that it is no longer needed
}

function completeCheckout(order) {
    const user = _.get(order, 'customer.user');
    const email = _.get(user, 'email');
    aliasOrIdentify(user);
    track({
        type: 'CompleteRegistration',
        data: {
            email,
        },
    });
    trackGoogleConversion({ id: 957569029, label: 'R8svCP7hhWIQhbDNyAM' });
    sendGoogleAnalyticsPageview('/signup'); // sending this pageview for now until confirmation that it is no longer needed
}

function trackSignupLaunched(referrer) {
    track({
        type: 'Customer Signup Launched',
        data: {
            custom_signup_version: 'legacy',
            referrer,
        },
    });
}

const lsTrackService = {
    track,
    page,
    aliasOrIdentify,
    setEngagementId,
    getAnonymousId,
    waitAndGetAnonymousId,
    recordAnonymousIdToOrder,
    sendGoogleAnalyticsPageview,
    trackGoogleConversion,
    initiateCheckout,
    completeCheckout,
    trackSignupLaunched,
    _private,
};

export default lsTrackService;
